import React from 'react';
import { toast } from 'sonner';
import { consoleLocale, t, type MessageKey } from '@core/i18n';
import { formatDashboardTimestamp } from '../../utils/timestamps';
import {
  DashboardTable,
  DashboardTableActionButton,
  DashboardTableBadge,
  DashboardTableCell,
  DashboardTableDetailsGrid,
  DashboardTableDetailsItem,
  DashboardTableDetailsPanel,
  DashboardTableHeader,
  DashboardTableHeaderCell,
  DashboardTableRow,
  DashboardTableState,
  DashboardTableStatus,
  dashboardTableColumns,
  useDashboardTablePagination,
  type DashboardTableTone,
} from '../../components/DashboardTable';
import {
  useDashboardConsoleSession,
  type DashboardConsoleSessionClaims,
} from '../../consoleSession';
import {
  useDashboardSelectedContext,
  useDashboardSelectedContextDisplay,
} from '../../selectedContext';
import { listDashboardEnvironments, listDashboardProjects } from '../../consoleContextApi';
import { useSiteRouter } from '@core/router/useSiteRouter';
import {
  getDashboardAuditActivity,
  listDashboardAuditEvents,
  type DashboardConsoleAuditActivityBin,
  type DashboardConsoleAuditCategory,
  type DashboardConsoleAuditEvent,
  type DashboardConsoleAuditOutcome,
  type DashboardConsolePolicyKind,
} from './consoleAuditApi';
import { downloadAuditEvents } from './downloadAuditEvents';
import { AuditActivityChart } from './AuditActivityChart';
import { AuditFilterChips, type AuditFilterDefinition } from './AuditFilterChips';
import {
  auditRangeFromPreset,
  bucketAuditActivity,
  canStepAuditRangeForward,
  centreAuditRange,
  clampAuditRangeToSpan,
  shiftAuditRange,
  DEFAULT_AUDIT_RANGE_PRESET_ID,
  type AuditRangePresetId,
  type AuditTimeRange,
} from './auditActivity';
import { DownloadIcon } from '../../icons/SidebarIcons';
import { listDashboardOrganizationMemberships } from '../team-members/consoleTeamRbacApi';
import {
  resolveDashboardIdentityPrimaryLabel,
  type DashboardIdentitySource,
} from '../../utils/userIdentity';
import { listDashboardAccountOrganizations } from '../account-settings/consoleAccountApi';

const CATEGORY_OPTIONS: readonly DashboardConsoleAuditCategory[] = [
  'POLICY',
  'SETTINGS',
  'KEY_EXPORT',
  'BILLING',
  'WEBHOOK',
  'API_KEY',
  'TEAM',
  'APPROVAL',
  'ORG_PROJECT_ENV',
  'RUNTIME_SNAPSHOT',
  'SYSTEM',
];

const OUTCOME_OPTIONS: readonly DashboardConsoleAuditOutcome[] = ['SUCCESS', 'FAILURE', 'PENDING'];

export type AuditApprovalDirectoryEntry = {
  id: string;
  policyId: string | null;
  resourceId: string | null;
  policyName: string | null;
  policyKind: DashboardConsolePolicyKind | null;
};

export type AuditApprovalIntegration = {
  listApprovals: (input: {
    projectId?: string;
    environmentId?: string;
  }) => Promise<readonly AuditApprovalDirectoryEntry[]>;
  policyPath: (input: {
    policyId: string;
    policyKind: DashboardConsolePolicyKind;
    approvalId?: string;
  }) => string;
};

type AuditLogsPageProps = {
  approvalIntegration?: AuditApprovalIntegration;
};

const AUDIT_CATEGORY_LABEL_KEYS = {
  POLICY: 'auditCategoryPolicy',
  SETTINGS: 'auditCategorySettings',
  KEY_EXPORT: 'auditCategoryKeyExport',
  BILLING: 'auditCategoryBilling',
  WEBHOOK: 'auditCategoryWebhook',
  API_KEY: 'auditCategoryApiKey',
  TEAM: 'auditCategoryTeam',
  APPROVAL: 'auditCategoryApproval',
  ORG_PROJECT_ENV: 'auditCategoryOrgProjectEnv',
  RUNTIME_SNAPSHOT: 'auditCategoryRuntimeSnapshot',
  SYSTEM: 'auditCategorySystem',
} as const satisfies Record<DashboardConsoleAuditCategory, MessageKey>;

const AUDIT_OUTCOME_LABEL_KEYS = {
  SUCCESS: 'auditOutcomeSuccess',
  FAILURE: 'auditOutcomeFailure',
  PENDING: 'auditOutcomePending',
} as const satisfies Record<DashboardConsoleAuditOutcome, MessageKey>;

function formatAuditCategoryLabel(category: DashboardConsoleAuditCategory): string {
  return t(AUDIT_CATEGORY_LABEL_KEYS[category]);
}

/* Badges show the raw wire enum in English, as before; other locales show the label. */
function formatAuditBadgeText(raw: string, label: string): string {
  return consoleLocale === 'en' ? raw : label;
}

function formatAuditActorType(actorType: string): string {
  if (actorType === 'USER') return formatAuditBadgeText(actorType, t('auditActorTypeUser'));
  if (actorType === 'SYSTEM') return formatAuditBadgeText(actorType, t('auditActorTypeSystem'));
  return actorType;
}

function formatAuditScopeTypeLabel(value: string): string {
  const normalized = readText(value).toUpperCase();
  if (normalized === 'ORG' || normalized === 'ORGANIZATION') return t('auditScopeTypeOrganization');
  if (normalized === 'PROJECT') return t('auditScopeTypeProject');
  if (normalized === 'ENVIRONMENT') return t('auditScopeTypeEnvironment');
  return humanizeMachineLabel(value);
}

const AUDIT_EVENTS_TABLE_COLUMNS = dashboardTableColumns(1.05, 2.1, 0.95, 1.1, 0.8, 0.75);
const AUDIT_EVENTS_LIMIT = 100;
/* Bar count is fixed rather than measured: at the card's width these land
   near 8px apiece with a hairline gap, which reads as a distribution
   without implying per-bar precision the bucketing does not have. */
const AUDIT_ACTIVITY_BIN_COUNT = 96;

function formatTimestamp(value: string): string {
  return formatDashboardTimestamp(value, '—');
}

function readText(value: unknown): string {
  return String(value || '').trim();
}

function humanizeMachineLabel(value: string): string {
  const tokens = readText(value)
    .split(/[._\-\s]+/g)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const normalized = entry.toLowerCase();
      if (normalized === 'api' || normalized === 'id' || normalized === 'mfa') {
        return normalized.toUpperCase();
      }
      return normalized;
    });
  if (tokens.length === 0) return '';
  const [first, ...rest] = tokens;
  const firstWord =
    first === first.toUpperCase() ? first : `${first.charAt(0).toUpperCase()}${first.slice(1)}`;
  return [firstWord, ...rest].join(' ');
}

function formatPolicyKindLabel(value: string): string {
  const normalized = readText(value).toUpperCase();
  if (normalized === 'GAS_SPONSORSHIP') return t('auditPolicyKindGasSponsorship');
  if (normalized === 'TRANSACTION') return t('auditPolicyKindTransaction');
  return humanizeMachineLabel(value);
}

function formatVersionLabel(value: unknown): string {
  const version = Number(value);
  if (!Number.isFinite(version) || version < 0) return '';
  return `v${Math.floor(version)}`;
}

function formatAmountMinorLabel(amountMinorRaw: unknown, currencyRaw: unknown): string {
  const amountMinor = Number(amountMinorRaw);
  const currency = readText(currencyRaw).toUpperCase() || 'USD';
  if (!Number.isFinite(amountMinor)) return '';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency}`;
  }
}

function appendDetail(details: string[], value: unknown): void {
  const normalized = readText(value);
  if (!normalized || details.includes(normalized)) return;
  details.push(normalized);
}

function readFirstText(...values: unknown[]): string {
  for (const value of values) {
    const normalized = readText(value);
    if (normalized) return normalized;
  }
  return '';
}

function buildAuditOrganizationDirectoryEntries(
  row: DashboardConsoleAuditEvent,
  organizationDirectory: Record<string, string>,
): Array<{ id: string; label: string }> {
  const candidates = [
    {
      id: readFirstText(row.metadata?.organizationId),
      label: readFirstText(row.metadata?.organizationName),
    },
    {
      id: readText(row.orgId),
      label: readFirstText(
        row.metadata?.organizationName,
        organizationDirectory[readText(row.orgId)],
      ),
    },
  ];
  const deduped = new Map<string, string>();
  for (const candidate of candidates) {
    const id = readText(candidate.id);
    if (!id || deduped.has(id)) continue;
    const label = readFirstText(candidate.label, organizationDirectory[id]);
    if (!label || label === id) continue;
    deduped.set(id, label);
  }
  return [...deduped.entries()].map(([id, label]) => ({ id, label }));
}

function replaceAuditOrganizationIdsInText(
  value: string,
  row: DashboardConsoleAuditEvent,
  organizationDirectory: Record<string, string>,
): string {
  let nextValue = readText(value);
  if (!nextValue) return '';
  for (const reference of buildAuditOrganizationDirectoryEntries(row, organizationDirectory)) {
    nextValue = nextValue.split(reference.id).join(reference.label);
  }
  return nextValue;
}

function buildDashboardPath(basePath: string, query: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const normalized = readText(value);
    if (!normalized) continue;
    searchParams.set(key, normalized);
  }
  const queryString = searchParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
}

function formatPolicyAuditEventTitle(row: DashboardConsoleAuditEvent): {
  title: string;
  detailParts: string[];
} | null {
  const action = readText(row.action).toLowerCase();
  if (!action.startsWith('policy.')) return null;

  const policyName = readText(row.policyName) || readText(row.metadata?.policyName);
  const policyKind = formatPolicyKindLabel(
    readText(row.policyKind) || readText(row.metadata?.policyKind),
  );
  const versionLabel = formatVersionLabel(row.metadata?.version);
  const details: string[] = [];

  if (action === 'policy.create') {
    appendDetail(details, policyName);
    appendDetail(details, policyKind);
    appendDetail(details, versionLabel);
    return {
      title: t('auditTitlePolicyCreated'),
      detailParts: details,
    };
  }

  if (action === 'policy.update') {
    appendDetail(details, policyName);
    appendDetail(details, policyKind);
    appendDetail(details, versionLabel);
    return {
      title: t('auditTitlePolicyUpdated'),
      detailParts: details,
    };
  }

  if (action === 'policy.delete') {
    appendDetail(details, policyName);
    appendDetail(details, policyKind);
    appendDetail(details, versionLabel);
    return {
      title: t('auditTitlePolicyDeleted'),
      detailParts: details,
    };
  }

  if (action === 'policy.publish') {
    appendDetail(details, policyName);
    appendDetail(details, policyKind);
    appendDetail(details, versionLabel);
    appendDetail(details, readText(row.metadata?.status));
    return {
      title: t('auditTitlePolicyPublished'),
      detailParts: details,
    };
  }

  if (action === 'policy.assignment.upsert') {
    appendDetail(details, policyName);
    appendDetail(details, policyKind);
    appendDetail(details, formatAuditScopeTypeLabel(readText(row.metadata?.assignmentScopeType)));
    return {
      title: t('auditTitlePolicyAssignmentUpdated'),
      detailParts: details,
    };
  }

  if (action === 'policy.assignment.delete') {
    appendDetail(details, policyName);
    appendDetail(details, policyKind);
    appendDetail(details, formatAuditScopeTypeLabel(readText(row.metadata?.assignmentScopeType)));
    return {
      title: t('auditTitlePolicyAssignmentRemoved'),
      detailParts: details,
    };
  }

  return null;
}

function formatBillingAuditEventTitle(
  row: DashboardConsoleAuditEvent,
  organizationDirectory: Record<string, string>,
): {
  title: string;
  detailParts: string[];
} | null {
  const action = readText(row.action).toLowerCase();
  if (!action.startsWith('billing.')) return null;

  const details: string[] = [];

  if (action === 'billing.credit_purchase.settled') {
    appendDetail(details, readText(row.metadata?.purchaseId));
    appendDetail(
      details,
      formatAmountMinorLabel(row.metadata?.amountMinor, row.metadata?.currency),
    );
    appendDetail(details, readText(row.metadata?.receiptId));
    appendDetail(details, readText(row.metadata?.providerCheckoutSessionRef));
    appendDetail(details, humanizeMachineLabel(readText(row.metadata?.settlementSource)));
    return {
      title: t('auditTitleCreditPurchaseSettled'),
      detailParts: details,
    };
  }

  if (action === 'billing.invoice.generated') {
    appendDetail(details, readText(row.metadata?.invoiceId));
    appendDetail(details, readText(row.metadata?.periodMonthUtc));
    appendDetail(
      details,
      formatAmountMinorLabel(row.metadata?.amountDueMinor, row.metadata?.currency),
    );
    appendDetail(details, humanizeMachineLabel(readText(row.metadata?.invoiceDocumentType)));
    return {
      title:
        readText(row.metadata?.generated) === 'true'
          ? t('auditTitleInvoiceGenerated')
          : t('auditTitleInvoiceRefreshed'),
      detailParts: details,
    };
  }

  if (action === 'billing.invoice.pdf_export') {
    appendDetail(details, readText(row.metadata?.invoiceId));
    appendDetail(details, readText(row.metadata?.periodMonthUtc));
    return {
      title: t('auditTitleInvoicePdfExported'),
      detailParts: details,
    };
  }

  if (action === 'billing.adjustment.support_credit') {
    const orgLabel = replaceAuditOrganizationIdsInText(
      readFirstText(
        row.metadata?.organizationName,
        organizationDirectory[readText(row.orgId)],
        row.orgId,
      ),
      row,
      organizationDirectory,
    );
    const resultingBalance = formatAmountMinorLabel(
      row.metadata?.resultingBalanceMinor,
      row.metadata?.currency,
    );
    const created = readText(row.metadata?.created);
    appendDetail(
      details,
      formatAmountMinorLabel(row.metadata?.amountMinor, row.metadata?.currency),
    );
    appendDetail(details, orgLabel);
    appendDetail(details, readText(row.metadata?.reasonCode));
    appendDetail(details, readFirstText(row.metadata?.relatedInvoiceId, row.metadata?.invoiceId));
    appendDetail(
      details,
      resultingBalance ? t('auditDetailBalance', { amount: resultingBalance }) : '',
    );
    appendDetail(details, readText(row.metadata?.adjustmentId));
    return {
      title:
        created === 'false'
          ? readText(row.metadata?.platformBilling) === 'true'
            ? t('auditTitleSupportCreditReusedCustomer')
            : t('auditTitleSupportCreditReused')
          : readText(row.metadata?.platformBilling) === 'true'
            ? t('auditTitleSupportCreditGrantedCustomer')
            : t('auditTitleSupportCreditGranted'),
      detailParts: details,
    };
  }

  if (action === 'billing.adjustment.admin_debit') {
    const orgLabel = replaceAuditOrganizationIdsInText(
      readFirstText(
        row.metadata?.organizationName,
        organizationDirectory[readText(row.orgId)],
        row.orgId,
      ),
      row,
      organizationDirectory,
    );
    const resultingBalance = formatAmountMinorLabel(
      row.metadata?.resultingBalanceMinor,
      row.metadata?.currency,
    );
    const created = readText(row.metadata?.created);
    appendDetail(
      details,
      formatAmountMinorLabel(row.metadata?.amountMinor, row.metadata?.currency),
    );
    appendDetail(details, orgLabel);
    appendDetail(details, readText(row.metadata?.reasonCode));
    appendDetail(details, readFirstText(row.metadata?.relatedInvoiceId, row.metadata?.invoiceId));
    appendDetail(
      details,
      resultingBalance ? t('auditDetailBalance', { amount: resultingBalance }) : '',
    );
    appendDetail(details, readText(row.metadata?.adjustmentId));
    return {
      title:
        created === 'false'
          ? readText(row.metadata?.platformBilling) === 'true'
            ? t('auditTitleAdminDebitReusedCustomer')
            : t('auditTitleAdminDebitReused')
          : readText(row.metadata?.platformBilling) === 'true'
            ? t('auditTitleAdminDebitAppliedCustomer')
            : t('auditTitleAdminDebitApplied'),
      detailParts: details,
    };
  }

  return null;
}

function formatWebhookAuditEventTitle(row: DashboardConsoleAuditEvent): {
  title: string;
  detailParts: string[];
} | null {
  const action = readText(row.action).toLowerCase();
  if (!action.startsWith('webhook.')) return null;
  const details: string[] = [];
  appendDetail(details, readText(row.metadata?.endpointId));
  appendDetail(details, readText(row.metadata?.deliveryId));

  if (action === 'webhook.endpoint.create') {
    return { title: t('auditTitleWebhookEndpointCreated'), detailParts: details };
  }
  if (action === 'webhook.endpoint.update') {
    return { title: t('auditTitleWebhookEndpointUpdated'), detailParts: details };
  }
  if (action === 'webhook.endpoint.delete') {
    return { title: t('auditTitleWebhookEndpointDeleted'), detailParts: details };
  }
  if (action === 'webhook.delivery.replay_requested') {
    return { title: t('auditTitleWebhookReplayRequested'), detailParts: details };
  }
  return null;
}

function formatAuditEventTitle(
  row: DashboardConsoleAuditEvent,
  organizationDirectory: Record<string, string>,
): {
  title: string;
  detailParts: string[];
} {
  const action = readText(row.action).toLowerCase();
  const approvalId = readText(row.metadata?.approvalId);
  const operationType = readText(row.metadata?.operationType);
  if (action.startsWith('approval.request.')) {
    const title =
      action === 'approval.request.create'
        ? t('auditTitleApprovalCreated')
        : action === 'approval.request.approve'
          ? t('auditTitleApprovalApproved')
          : action === 'approval.request.reject'
            ? t('auditTitleApprovalRejected')
            : t('auditTitleApprovalUpdated');
    return {
      title,
      detailParts: [humanizeMachineLabel(operationType), approvalId].filter(Boolean),
    };
  }

  const policyDisplay = formatPolicyAuditEventTitle(row);
  if (policyDisplay) return policyDisplay;

  const billingDisplay = formatBillingAuditEventTitle(row, organizationDirectory);
  if (billingDisplay) return billingDisplay;

  const webhookDisplay = formatWebhookAuditEventTitle(row);
  if (webhookDisplay) return webhookDisplay;

  const title =
    replaceAuditOrganizationIdsInText(readText(row.summary), row, organizationDirectory) ||
    humanizeMachineLabel(row.action) ||
    row.id;
  const detail = humanizeMachineLabel(row.action);
  return {
    title,
    detailParts: detail && detail.toLowerCase() !== title.toLowerCase() ? [detail] : [],
  };
}

function formatAuditActor(
  row: DashboardConsoleAuditEvent,
  memberDirectory: Record<string, DashboardIdentitySource>,
  sessionClaims: DashboardConsoleSessionClaims | null,
): {
  primary: string;
  secondary: string;
} {
  const userId = readText(row.actorUserId);
  const identity = memberDirectory[userId] || { userId };
  const primary = resolveDashboardIdentityPrimaryLabel(identity, sessionClaims);
  if (row.actorType === 'SYSTEM') {
    return {
      primary,
      secondary: t('auditActorTypeSystem'),
    };
  }
  return {
    primary,
    secondary: userId && userId !== primary ? userId : '',
  };
}

function outcomeTone(outcome: DashboardConsoleAuditOutcome): DashboardTableTone {
  if (outcome === 'SUCCESS') return 'success';
  if (outcome === 'FAILURE') return 'danger';
  return 'warning';
}

function resolveAuditProjectLabel(input: {
  projectId: string;
  projectDirectory: Record<string, string>;
  selectedProjectId: string;
  selectedProjectLabel: string;
}): string {
  if (!input.projectId) return '-';
  if (input.projectDirectory[input.projectId]) return input.projectDirectory[input.projectId]!;
  if (input.selectedProjectId === input.projectId && input.selectedProjectLabel) {
    return input.selectedProjectLabel;
  }
  return input.projectId;
}

function resolveAuditEnvironmentLabel(input: {
  environmentId: string;
  environmentDirectory: Record<string, string>;
  selectedEnvironmentId: string;
  selectedEnvironmentLabel: string;
}): string {
  if (!input.environmentId) return '-';
  if (input.environmentDirectory[input.environmentId]) {
    return input.environmentDirectory[input.environmentId]!;
  }
  if (input.selectedEnvironmentId === input.environmentId && input.selectedEnvironmentLabel) {
    return input.selectedEnvironmentLabel;
  }
  return input.environmentId;
}

function resolveAuditScopeLabels(input: {
  row: DashboardConsoleAuditEvent;
  projectDirectory: Record<string, string>;
  environmentDirectory: Record<string, string>;
  selectedProjectId: string;
  selectedProjectLabel: string;
  selectedEnvironmentId: string;
  selectedEnvironmentLabel: string;
}): {
  projectLabel: string;
  environmentLabel: string;
} {
  const projectId = readText(input.row.projectId);
  const environmentId = readText(input.row.environmentId);
  const projectLabel = projectId
    ? resolveAuditProjectLabel({
        projectId,
        projectDirectory: input.projectDirectory,
        selectedProjectId: input.selectedProjectId,
        selectedProjectLabel: input.selectedProjectLabel,
      })
    : '';
  const environmentLabel = environmentId
    ? resolveAuditEnvironmentLabel({
        environmentId,
        environmentDirectory: input.environmentDirectory,
        selectedEnvironmentId: input.selectedEnvironmentId,
        selectedEnvironmentLabel: input.selectedEnvironmentLabel,
      })
    : '';
  return {
    projectLabel,
    environmentLabel,
  };
}

function parseAuditEventTimestamp(value: string): number {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isOrgScopedAuditEvent(row: DashboardConsoleAuditEvent): boolean {
  return !readText(row.projectId) && !readText(row.environmentId);
}

function mergeAuditEvents(
  batches: ReadonlyArray<ReadonlyArray<DashboardConsoleAuditEvent>>,
): DashboardConsoleAuditEvent[] {
  const deduped = new Map<string, DashboardConsoleAuditEvent>();
  for (const batch of batches) {
    for (const row of batch) {
      const id = readText(row.id);
      if (!id || deduped.has(id)) continue;
      deduped.set(id, row);
    }
  }
  return [...deduped.values()]
    .sort((a, b) => {
      const tsDiff = parseAuditEventTimestamp(b.createdAt) - parseAuditEventTimestamp(a.createdAt);
      if (tsDiff !== 0) return tsDiff;
      return b.id.localeCompare(a.id);
    })
    .slice(0, AUDIT_EVENTS_LIMIT);
}

function renderAuditLinkedIdentifier(input: {
  id: string;
  label?: string;
  href?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}): React.JSX.Element {
  const id = readText(input.id);
  if (!id) return <span>-</span>;
  const label = readText(input.label) || id;
  return (
    <div>
      {input.href ? (
        <a className="dashboard-inline-link" href={input.href} onClick={input.onClick} title={id}>
          {label}
        </a>
      ) : (
        <span>{label}</span>
      )}
      {label !== id ? (
        <>
          <br />
          <code>{id}</code>
        </>
      ) : null}
    </div>
  );
}

export function AuditLogsPage({ approvalIntegration }: AuditLogsPageProps): React.JSX.Element {
  const session = useDashboardConsoleSession();
  const selectedContext = useDashboardSelectedContext();
  const selectedContextDisplay = useDashboardSelectedContextDisplay();
  const { linkProps } = useSiteRouter();
  const selectedProjectId = String(selectedContext.project || '').trim();
  const selectedEnvironmentId = String(selectedContext.environment || '').trim();
  const selectedProjectLabel = String(selectedContextDisplay.project || '').trim();
  const selectedEnvironmentLabel = String(selectedContextDisplay.environment || '').trim();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [events, setEvents] = React.useState<DashboardConsoleAuditEvent[]>([]);
  /* Server counts over the whole range; null until they arrive or when the
     request fails, and the chart then counts the listed events instead. */
  const [serverActivityBins, setServerActivityBins] = React.useState<
    DashboardConsoleAuditActivityBin[] | null
  >(null);
  const [searchInput, setSearchInput] = React.useState<string>('');
  const [debouncedSearchInput, setDebouncedSearchInput] = React.useState<string>('');
  const [eventCategoryFilter, setEventCategoryFilter] = React.useState<string>('');
  const [eventOutcomeFilter, setEventOutcomeFilter] = React.useState<string>('');
  const [eventActorFilter, setEventActorFilter] = React.useState<string>('');
  /* One clock read seeds the window and then anchors every "is this the
     present?" check, so the range cannot drift under a re-render. */
  const [nowMs, setNowMs] = React.useState<number>(() => Date.now());
  const [range, setRange] = React.useState<AuditTimeRange>(() =>
    auditRangeFromPreset(DEFAULT_AUDIT_RANGE_PRESET_ID, Date.now()),
  );
  const [selection, setSelection] = React.useState<AuditTimeRange | null>(null);
  const [expandedEventId, setExpandedEventId] = React.useState<string>('');
  const [memberDirectory, setMemberDirectory] = React.useState<
    Record<string, DashboardIdentitySource>
  >({});
  const [organizationDirectory, setOrganizationDirectory] = React.useState<Record<string, string>>(
    {},
  );
  const [projectDirectory, setProjectDirectory] = React.useState<Record<string, string>>({});
  const [environmentDirectory, setEnvironmentDirectory] = React.useState<Record<string, string>>(
    {},
  );
  const [approvalDirectory, setApprovalDirectory] = React.useState<
    Record<string, AuditApprovalDirectoryEntry>
  >({});
  const resolvedOrganizationDirectory = React.useMemo(() => {
    const nextDirectory = { ...organizationDirectory };
    for (const row of events) {
      const organizationId = readText(row.metadata?.organizationId);
      const organizationName = readText(row.metadata?.organizationName);
      if (!organizationId || !organizationName) continue;
      nextDirectory[organizationId] = organizationName;
    }
    return nextDirectory;
  }, [events, organizationDirectory]);
  const eventsPagination = useDashboardTablePagination(events, {
    disabled: loading,
    initialRowsPerPage: 10,
    itemLabel: t('auditEventItemLabel'),
    itemLabelPlural: t('auditEventItemLabelPlural'),
  });
  const setEventsPage = eventsPagination.setPage;
  const auditTablePagination = React.useMemo(
    () => ({
      ...eventsPagination.pagination,
      onPageChange: (page: number) => {
        setExpandedEventId('');
        eventsPagination.setPage(page);
      },
      onRowsPerPageChange: (rowsPerPage: number) => {
        setExpandedEventId('');
        eventsPagination.setRowsPerPage(rowsPerPage);
      },
    }),
    [eventsPagination, setExpandedEventId],
  );

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchInput(String(searchInput || '').trim());
    }, 250);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  React.useEffect(() => {
    if (session.loading) return;
    if (!session.claims) {
      setOrganizationDirectory({});
      setMemberDirectory({});
      return;
    }
    let cancelled = false;
    Promise.allSettled([
      listDashboardAccountOrganizations(),
      listDashboardOrganizationMemberships('all'),
    ])
      .then((results) => {
        if (cancelled) return;
        const organizations =
          results[0]?.status === 'fulfilled' && Array.isArray(results[0].value)
            ? results[0].value
            : [];
        const members =
          results[1]?.status === 'fulfilled' && Array.isArray(results[1].value)
            ? results[1].value
            : [];
        const nextOrganizationDirectory: Record<string, string> = {};
        for (const organization of organizations) {
          const organizationId = readText(organization.id);
          if (!organizationId) continue;
          nextOrganizationDirectory[organizationId] = readText(organization.name) || organizationId;
        }
        setOrganizationDirectory(nextOrganizationDirectory);
        const nextDirectory: Record<string, DashboardIdentitySource> = {};
        for (const member of members) {
          const userId = readText(member.userId);
          if (!userId) continue;
          nextDirectory[userId] = {
            userId,
            email: readText(member.email),
            displayName: readText(member.displayName),
          };
        }
        setMemberDirectory(nextDirectory);
      })
      .catch(() => {
        if (cancelled) return;
        setOrganizationDirectory({});
        setMemberDirectory({});
      });
    return () => {
      cancelled = true;
    };
  }, [session.claims, session.claims?.orgId, session.claims?.userId, session.loading]);

  React.useEffect(() => {
    if (session.loading) return;
    if (!session.claims) {
      setApprovalDirectory({});
      return;
    }
    let cancelled = false;
    if (!approvalIntegration) {
      setApprovalDirectory({});
      return;
    }
    approvalIntegration
      .listApprovals({
        ...(selectedProjectId ? { projectId: selectedProjectId } : {}),
        ...(selectedEnvironmentId ? { environmentId: selectedEnvironmentId } : {}),
      })
      .then((approvals) => {
        if (cancelled) return;
        const nextDirectory: Record<string, AuditApprovalDirectoryEntry> = {};
        for (const approval of approvals) {
          const approvalId = readText(approval.id);
          if (!approvalId) continue;
          nextDirectory[approvalId] = approval;
        }
        setApprovalDirectory(nextDirectory);
      })
      .catch(() => {
        if (cancelled) return;
        setApprovalDirectory({});
      });
    return () => {
      cancelled = true;
    };
  }, [
    approvalIntegration,
    selectedEnvironmentId,
    selectedProjectId,
    session.claims,
    session.claims?.orgId,
    session.loading,
  ]);

  React.useEffect(() => {
    if (session.loading) return;
    if (!session.claims) {
      setProjectDirectory({});
      setEnvironmentDirectory({});
      return;
    }
    let cancelled = false;
    Promise.allSettled([
      listDashboardProjects(),
      listDashboardEnvironments(selectedProjectId ? { projectId: selectedProjectId } : {}),
    ])
      .then((results) => {
        if (cancelled) return;
        const projects =
          results[0]?.status === 'fulfilled' && Array.isArray(results[0].value)
            ? results[0].value
            : [];
        const environments =
          results[1]?.status === 'fulfilled' && Array.isArray(results[1].value)
            ? results[1].value
            : [];
        const nextProjects: Record<string, string> = {};
        for (const project of projects) {
          const projectId = readText(project.id);
          if (!projectId) continue;
          nextProjects[projectId] = readText(project.name) || projectId;
        }
        const nextEnvironments: Record<string, string> = {};
        for (const environment of environments) {
          const environmentId = readText(environment.id);
          if (!environmentId) continue;
          nextEnvironments[environmentId] = readText(environment.name) || environmentId;
        }
        setProjectDirectory(nextProjects);
        setEnvironmentDirectory(nextEnvironments);
      })
      .catch(() => {
        if (cancelled) return;
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, session.claims, session.claims?.orgId, session.loading]);

  const copyAuditScopeValue = React.useCallback(
    async (value: string, label: 'Project' | 'Environment') => {
      const normalized = readText(value);
      if (!normalized) return;
      try {
        if (!window.navigator?.clipboard?.writeText) {
          throw new Error('clipboard_unavailable');
        }
        await window.navigator.clipboard.writeText(normalized);
        toast.success(t(label === 'Project' ? 'auditProjectIdCopied' : 'auditEnvironmentIdCopied'));
      } catch {
        toast.error(
          t(label === 'Project' ? 'auditProjectIdCopyFailed' : 'auditEnvironmentIdCopyFailed'),
        );
      }
    },
    [],
  );

  const loadAuditEvents = React.useCallback(() => {
    if (!session.claims) {
      setLoading(false);
      setEvents([]);
      setEventsPage(1);
      setExpandedEventId('');
      setErrorMessage(session.errorMessage || t('auditSessionUnavailable'));
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorMessage('');

    const baseRequest = {
      ...(eventCategoryFilter
        ? { category: eventCategoryFilter as DashboardConsoleAuditCategory }
        : {}),
      ...(eventOutcomeFilter
        ? { outcome: eventOutcomeFilter as DashboardConsoleAuditOutcome }
        : {}),
      ...(eventActorFilter ? { actorUserId: eventActorFilter } : {}),
      ...(debouncedSearchInput ? { q: debouncedSearchInput } : {}),
      from: new Date(range.fromMs).toISOString(),
      to: new Date(range.toMs).toISOString(),
      limit: AUDIT_EVENTS_LIMIT,
    };
    const scopedRequest = {
      ...baseRequest,
      ...(selectedProjectId ? { projectId: selectedProjectId } : {}),
      ...(selectedEnvironmentId ? { environmentId: selectedEnvironmentId } : {}),
    };
    const shouldIncludeOrgScopedRows = Boolean(selectedProjectId || selectedEnvironmentId);
    setServerActivityBins(null);
    const { limit: _limit, ...activityFilter } = scopedRequest;
    getDashboardAuditActivity({
      ...activityFilter,
      bins: AUDIT_ACTIVITY_BIN_COUNT,
      includeOrgScoped: shouldIncludeOrgScopedRows,
    })
      .then((bins) => {
        if (cancelled || bins.length !== AUDIT_ACTIVITY_BIN_COUNT) return;
        setServerActivityBins(bins);
      })
      .catch(() => undefined);
    const request = shouldIncludeOrgScopedRows
      ? Promise.all([
          listDashboardAuditEvents(scopedRequest),
          listDashboardAuditEvents(baseRequest).then((rows) => rows.filter(isOrgScopedAuditEvent)),
        ]).then(([scopedRows, orgScopedRows]) => mergeAuditEvents([scopedRows, orgScopedRows]))
      : listDashboardAuditEvents(scopedRequest);

    request
      .then((nextEvents) => {
        if (cancelled) return;
        setEvents(nextEvents);
        setEventsPage(1);
        setExpandedEventId('');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setEvents([]);
        setEventsPage(1);
        setExpandedEventId('');
        setErrorMessage(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    debouncedSearchInput,
    eventActorFilter,
    eventCategoryFilter,
    eventOutcomeFilter,
    setEventsPage,
    range.fromMs,
    range.toMs,
    selectedEnvironmentId,
    selectedProjectId,
    session.claims,
    session.errorMessage,
  ]);

  React.useEffect(() => {
    if (session.loading) {
      setLoading(true);
      return;
    }
    const cleanup = loadAuditEvents();
    return cleanup;
  }, [loadAuditEvents, session.loading]);

  const activityBins = React.useMemo(
    () => serverActivityBins ?? bucketAuditActivity(events, range, AUDIT_ACTIVITY_BIN_COUNT),
    [events, range, serverActivityBins],
  );

  /* Actor options come from the rows on screen rather than from the whole
     directory: filtering to somebody with no events in this window would
     only ever return nothing. */
  const actorOptions = React.useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of events) {
      const actorUserId = readText(row.actorUserId);
      if (!actorUserId || seen.has(actorUserId)) continue;
      const identity = memberDirectory[actorUserId] || { userId: actorUserId };
      seen.set(actorUserId, resolveDashboardIdentityPrimaryLabel(identity, session.claims));
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [events, memberDirectory, session.claims]);

  const filterDefinitions = React.useMemo<AuditFilterDefinition[]>(
    () => [
      {
        id: 'category',
        label: t('auditFilterCategory'),
        value: eventCategoryFilter,
        options: CATEGORY_OPTIONS.map((entry) => ({
          value: entry,
          label: formatAuditCategoryLabel(entry),
        })),
      },
      {
        id: 'outcome',
        label: t('auditFilterOutcome'),
        value: eventOutcomeFilter,
        options: OUTCOME_OPTIONS.map((entry) => ({
          value: entry,
          label: t(AUDIT_OUTCOME_LABEL_KEYS[entry]),
        })),
      },
      { id: 'actor', label: t('auditFilterActor'), value: eventActorFilter, options: actorOptions },
    ],
    [actorOptions, eventActorFilter, eventCategoryFilter, eventOutcomeFilter],
  );

  const onFilterChange = React.useCallback((id: string, value: string) => {
    if (id === 'category') setEventCategoryFilter(value);
    else if (id === 'outcome') setEventOutcomeFilter(value);
    else if (id === 'actor') setEventActorFilter(value);
  }, []);

  const onClearFilters = React.useCallback(() => {
    setEventCategoryFilter('');
    setEventOutcomeFilter('');
    setEventActorFilter('');
  }, []);

  /* Every range move re-reads the clock: a window opened an hour ago should
     step relative to the present, not to the render that seeded it. */
  const onPresetChange = React.useCallback((next: AuditRangePresetId) => {
    const atMs = Date.now();
    setNowMs(atMs);
    setSelection(null);
    setRange(auditRangeFromPreset(next, atMs));
  }, []);

  const onStepRange = React.useCallback((direction: -1 | 1) => {
    const atMs = Date.now();
    setNowMs(atMs);
    setSelection(null);
    setRange((current) => shiftAuditRange(current, direction, atMs));
  }, []);

  const onJumpToTimestamp = React.useCallback((atMs: number) => {
    const clockMs = Date.now();
    setNowMs(clockMs);
    setSelection(null);
    setRange((current) => centreAuditRange(current, atMs, clockMs));
  }, []);

  const onFocusSelection = React.useCallback(() => {
    if (!selection) return;
    setNowMs(Date.now());
    setRange(clampAuditRangeToSpan(selection, range));
    setSelection(null);
  }, [range, selection]);

  const onExportEvents = React.useCallback(() => {
    try {
      downloadAuditEvents(events);
    } catch (error: unknown) {
      toast.error(t('auditExportFailed'), {
        description: error instanceof Error ? error.message : String(error),
      });
    }
  }, [events]);

  return (
    <div className="dashboard-view" aria-label={t('auditPageLabel')}>
      <section
        className="dashboard-view__section dashboard-audit-section--plain"
        aria-label={t('auditEventFilters')}
      >
        <div className="dashboard-audit-toolbar">
          <label className="dashboard-search-control dashboard-search-control--compact dashboard-audit-toolbar__search">
            <span className="dashboard-search-icon" aria-hidden="true" />
            <input
              type="search"
              aria-label={t('auditSearchEvents')}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t('auditSearchPlaceholder')}
            />
          </label>
          <button
            type="button"
            className="dashboard-pagination-button dashboard-pagination-button--secondary dashboard-pagination-button--with-icon"
            onClick={onExportEvents}
            disabled={loading || events.length === 0}
            title={t('auditExportTooltip', { count: events.length })}
          >
            <DownloadIcon size={15} strokeWidth={1.75} />
            {t('auditExportShown')}
          </button>
        </div>

        <AuditFilterChips
          filters={filterDefinitions}
          onChange={onFilterChange}
          onClearAll={onClearFilters}
        />

        <AuditActivityChart
          bins={activityBins}
          range={range}
          nowMs={nowMs}
          loading={loading}
          selection={selection}
          canStepForward={canStepAuditRangeForward(range, nowMs)}
          onPresetChange={onPresetChange}
          onStepRange={onStepRange}
          onSelectionChange={setSelection}
          onFocusSelection={onFocusSelection}
          onJumpToTimestamp={onJumpToTimestamp}
        />

        {errorMessage ? <p className="dashboard-pagination-note">{errorMessage}</p> : null}
      </section>

      <section
        className="dashboard-view__section dashboard-audit-section--plain"
        aria-label={t('auditEventsTableLabel')}
      >
        <DashboardTable
          ariaLabel={t('auditEventsLabel')}
          columns={AUDIT_EVENTS_TABLE_COLUMNS}
          pagination={auditTablePagination}
        >
          <DashboardTableHeader>
            <DashboardTableHeaderCell>{t('auditTimestamp')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('auditEvent')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('auditColumnUser')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('auditColumnScope')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('auditColumnOutcome')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('auditDetails')}</DashboardTableHeaderCell>
          </DashboardTableHeader>
          {events.length === 0 ? (
            <DashboardTableState>
              {loading ? t('auditLoadingEvents') : t('auditNoEvents')}
            </DashboardTableState>
          ) : (
            eventsPagination.rows.map((row) => {
              const isExpanded = expandedEventId === row.id;
              const eventDisplay = formatAuditEventTitle(row, resolvedOrganizationDirectory);
              const actorDisplay = formatAuditActor(row, memberDirectory, session.claims);
              const scopeDisplay = resolveAuditScopeLabels({
                row,
                projectDirectory,
                environmentDirectory,
                selectedProjectId,
                selectedProjectLabel,
                selectedEnvironmentId,
                selectedEnvironmentLabel,
              });
              const actorUserId = readText(row.actorUserId);
              const approvalId = readText(row.metadata?.approvalId);
              const approval = approvalId ? approvalDirectory[approvalId] : null;
              const linkedPolicyId = readFirstText(
                row.policyId,
                row.metadata?.policyId,
                approval?.policyId,
                approval?.resourceId,
              );
              const linkedPolicyLabel =
                readFirstText(row.policyName, row.metadata?.policyName, approval?.policyName) ||
                linkedPolicyId;
              const linkedPolicyKind = row.policyKind ?? approval?.policyKind ?? null;
              const policyLink =
                linkedPolicyId && linkedPolicyKind && approvalIntegration
                  ? linkProps(
                      approvalIntegration.policyPath({
                        policyId: linkedPolicyId,
                        policyKind: linkedPolicyKind,
                      }),
                    )
                  : null;
              const approvalLink =
                approvalId && linkedPolicyId && linkedPolicyKind && approvalIntegration
                  ? linkProps(
                      approvalIntegration.policyPath({
                        policyId: linkedPolicyId,
                        policyKind: linkedPolicyKind,
                        approvalId,
                      }),
                    )
                  : null;
              const invoiceId = readFirstText(
                row.metadata?.invoiceId,
                row.metadata?.relatedInvoiceId,
              );
              const invoiceLink = invoiceId
                ? linkProps(`/dashboard/invoices/${encodeURIComponent(invoiceId)}`)
                : null;
              const receiptId = readText(row.metadata?.receiptId);
              const receiptLink = receiptId
                ? linkProps(`/dashboard/invoices/${encodeURIComponent(receiptId)}`)
                : null;
              const purchaseId = readText(row.metadata?.purchaseId);
              const adjustmentId = readText(row.metadata?.adjustmentId);
              const reasonCode = readText(row.metadata?.reasonCode);
              const adjustmentNote = readText(row.metadata?.note);
              const resultingBalanceLabel = formatAmountMinorLabel(
                row.metadata?.resultingBalanceMinor,
                row.metadata?.currency,
              );
              const linkedOrganizationId = readFirstText(row.metadata?.organizationId, row.orgId);
              const linkedOrganizationLabel = replaceAuditOrganizationIdsInText(
                readFirstText(
                  row.metadata?.organizationName,
                  resolvedOrganizationDirectory[linkedOrganizationId],
                  linkedOrganizationId,
                ),
                row,
                resolvedOrganizationDirectory,
              );
              const webhookEndpointId = readText(row.metadata?.endpointId);
              const webhookDeliveryId = readText(row.metadata?.deliveryId);
              const webhookEndpointLink = webhookEndpointId
                ? linkProps(
                    buildDashboardPath('/dashboard/webhooks', {
                      endpointId: webhookEndpointId,
                    }),
                  )
                : null;
              const webhookDeliveryLink =
                webhookEndpointId && webhookDeliveryId
                  ? linkProps(
                      buildDashboardPath('/dashboard/webhooks', {
                        endpointId: webhookEndpointId,
                        deliveryId: webhookDeliveryId,
                      }),
                    )
                  : null;
              const scopeType =
                readFirstText(row.metadata?.scopeType, row.metadata?.assignmentScopeType) ||
                (readText(row.environmentId)
                  ? 'ENVIRONMENT'
                  : readText(row.projectId)
                    ? 'PROJECT'
                    : 'ORG');
              const scopeId =
                readFirstText(
                  row.metadata?.scopeId,
                  row.metadata?.assignmentScopeId,
                  row.environmentId,
                  row.projectId,
                  row.orgId,
                ) || '';
              const projectLabel = row.projectId
                ? resolveAuditProjectLabel({
                    projectId: readText(row.projectId),
                    projectDirectory,
                    selectedProjectId,
                    selectedProjectLabel,
                  })
                : '';
              const environmentLabel = row.environmentId
                ? resolveAuditEnvironmentLabel({
                    environmentId: readText(row.environmentId),
                    environmentDirectory,
                    selectedEnvironmentId,
                    selectedEnvironmentLabel,
                  })
                : '';
              return (
                <React.Fragment key={row.id}>
                  <DashboardTableRow
                    className={
                      isExpanded
                        ? 'dashboard-audit-events__row dashboard-audit-events__row--expanded'
                        : 'dashboard-audit-events__row'
                    }
                  >
                    <DashboardTableCell truncate>
                      <span>{formatTimestamp(row.createdAt)}</span>
                    </DashboardTableCell>
                    <DashboardTableCell className="dashboard-data-table__cell--event">
                      <strong className="dashboard-data-table__summary">
                        {eventDisplay.title}
                      </strong>
                      <span className="dashboard-data-table__subline">
                        <DashboardTableBadge>
                          {formatAuditBadgeText(
                            row.category,
                            formatAuditCategoryLabel(row.category),
                          )}
                        </DashboardTableBadge>
                        {eventDisplay.detailParts.map((detail, index) => (
                          <span key={`${detail}-${index}`}>{detail}</span>
                        ))}
                      </span>
                    </DashboardTableCell>
                    <DashboardTableCell>
                      <strong className="dashboard-data-table__summary">
                        {actorDisplay.primary}
                      </strong>
                      {actorDisplay.secondary ? (
                        <span className="dashboard-data-table__subline dashboard-data-table__subline--muted">
                          {actorDisplay.secondary}
                        </span>
                      ) : null}
                    </DashboardTableCell>
                    <DashboardTableCell>
                      {scopeDisplay.projectLabel ? (
                        <span>
                          <strong>{t('auditScopeProject')}</strong> {scopeDisplay.projectLabel}
                        </span>
                      ) : null}
                      {scopeDisplay.environmentLabel ? (
                        <span>
                          <strong>{t('auditScopeEnvironment')}</strong>{' '}
                          {scopeDisplay.environmentLabel}
                        </span>
                      ) : null}
                      {!scopeDisplay.projectLabel && !scopeDisplay.environmentLabel ? (
                        <span>{t('auditScopeOrganization')}</span>
                      ) : null}
                    </DashboardTableCell>
                    <DashboardTableCell>
                      <DashboardTableStatus tone={outcomeTone(row.outcome)}>
                        {formatAuditBadgeText(
                          row.outcome,
                          t(AUDIT_OUTCOME_LABEL_KEYS[row.outcome]),
                        )}
                      </DashboardTableStatus>
                    </DashboardTableCell>
                    <DashboardTableCell
                      className="dashboard-data-table__cell--details"
                      align="center"
                    >
                      <DashboardTableActionButton
                        className="dashboard-audit-events__toggle"
                        aria-expanded={isExpanded}
                        onClick={() =>
                          setExpandedEventId((current) => (current === row.id ? '' : row.id))
                        }
                      >
                        {isExpanded ? t('auditHide') : t('auditView')}
                      </DashboardTableActionButton>
                    </DashboardTableCell>
                  </DashboardTableRow>
                  <DashboardTableDetailsPanel
                    className={
                      isExpanded
                        ? 'dashboard-audit-events__details-panel is-expanded'
                        : 'dashboard-audit-events__details-panel'
                    }
                    aria-hidden={!isExpanded}
                  >
                    <div className="dashboard-audit-events__details-content">
                      <DashboardTableDetailsGrid>
                        <DashboardTableDetailsItem label={t('auditDetailEventId')}>
                          <span>{row.id}</span>
                        </DashboardTableDetailsItem>
                        <DashboardTableDetailsItem label={t('auditDetailAction')}>
                          <span>{row.action || '-'}</span>
                        </DashboardTableDetailsItem>
                        <DashboardTableDetailsItem label={t('auditDetailActor')}>
                          <span>{actorDisplay.primary || '-'}</span>
                        </DashboardTableDetailsItem>
                        <DashboardTableDetailsItem label={t('auditDetailActorId')}>
                          {actorUserId ? <code>{actorUserId}</code> : <span>-</span>}
                        </DashboardTableDetailsItem>
                        <DashboardTableDetailsItem label={t('auditDetailActorType')}>
                          <span>{formatAuditActorType(row.actorType) || '-'}</span>
                        </DashboardTableDetailsItem>
                        <DashboardTableDetailsItem label={t('auditDetailPolicy')}>
                          {renderAuditLinkedIdentifier({
                            id: linkedPolicyId,
                            label: linkedPolicyLabel,
                            href: policyLink?.href,
                            onClick: policyLink?.onClick,
                          })}
                        </DashboardTableDetailsItem>
                        <DashboardTableDetailsItem label={t('auditDetailApproval')}>
                          {renderAuditLinkedIdentifier({
                            id: approvalId,
                            href: approvalLink?.href,
                            onClick: approvalLink?.onClick,
                          })}
                        </DashboardTableDetailsItem>
                        {purchaseId ? (
                          <DashboardTableDetailsItem label={t('auditDetailPurchase')}>
                            <code>{purchaseId}</code>
                          </DashboardTableDetailsItem>
                        ) : null}
                        {adjustmentId ? (
                          <DashboardTableDetailsItem label={t('auditDetailAdjustment')}>
                            <code>{adjustmentId}</code>
                          </DashboardTableDetailsItem>
                        ) : null}
                        {invoiceId ? (
                          <DashboardTableDetailsItem label={t('auditDetailInvoice')}>
                            {renderAuditLinkedIdentifier({
                              id: invoiceId,
                              href: invoiceLink?.href,
                              onClick: invoiceLink?.onClick,
                            })}
                          </DashboardTableDetailsItem>
                        ) : null}
                        {reasonCode ? (
                          <DashboardTableDetailsItem label={t('auditDetailReasonCode')}>
                            <code>{reasonCode}</code>
                          </DashboardTableDetailsItem>
                        ) : null}
                        {resultingBalanceLabel ? (
                          <DashboardTableDetailsItem label={t('auditDetailResultingBalance')}>
                            <span>{resultingBalanceLabel}</span>
                          </DashboardTableDetailsItem>
                        ) : null}
                        {adjustmentNote ? (
                          <DashboardTableDetailsItem label={t('auditDetailNote')}>
                            <span>{adjustmentNote}</span>
                          </DashboardTableDetailsItem>
                        ) : null}
                        {linkedOrganizationId ? (
                          <DashboardTableDetailsItem label={t('auditDetailOrganization')}>
                            <div>
                              <span>{linkedOrganizationLabel || linkedOrganizationId}</span>
                              {linkedOrganizationLabel &&
                              linkedOrganizationLabel !== linkedOrganizationId ? (
                                <>
                                  <br />
                                  <code>{linkedOrganizationId}</code>
                                </>
                              ) : null}
                            </div>
                          </DashboardTableDetailsItem>
                        ) : null}
                        {receiptId ? (
                          <DashboardTableDetailsItem label={t('auditDetailReceipt')}>
                            {renderAuditLinkedIdentifier({
                              id: receiptId,
                              href: receiptLink?.href,
                              onClick: receiptLink?.onClick,
                            })}
                          </DashboardTableDetailsItem>
                        ) : null}
                        {webhookEndpointId ? (
                          <DashboardTableDetailsItem label={t('auditDetailWebhookEndpoint')}>
                            {renderAuditLinkedIdentifier({
                              id: webhookEndpointId,
                              href: webhookEndpointLink?.href,
                              onClick: webhookEndpointLink?.onClick,
                            })}
                          </DashboardTableDetailsItem>
                        ) : null}
                        {webhookDeliveryId ? (
                          <DashboardTableDetailsItem label={t('auditDetailWebhookDelivery')}>
                            {renderAuditLinkedIdentifier({
                              id: webhookDeliveryId,
                              href: webhookDeliveryLink?.href,
                              onClick: webhookDeliveryLink?.onClick,
                            })}
                          </DashboardTableDetailsItem>
                        ) : null}
                        <DashboardTableDetailsItem label={t('auditDetailScopeType')}>
                          <span>
                            {scopeType === 'ORG'
                              ? t('auditScopeTypeOrganization')
                              : scopeType
                                ? formatAuditScopeTypeLabel(scopeType)
                                : t('auditScopeTypeOrganization')}
                          </span>
                        </DashboardTableDetailsItem>
                        <DashboardTableDetailsItem label={t('auditDetailScopeId')}>
                          {scopeId ? <code>{scopeId}</code> : <span>-</span>}
                        </DashboardTableDetailsItem>
                        <DashboardTableDetailsItem label={t('auditDetailProject')}>
                          {row.projectId ? (
                            <div>
                              <button
                                type="button"
                                className="dashboard-audit-events__copy-value"
                                title={row.projectId}
                                onClick={() =>
                                  void copyAuditScopeValue(row.projectId || '', 'Project')
                                }
                              >
                                {projectLabel}
                              </button>
                              {projectLabel !== readText(row.projectId) ? (
                                <>
                                  <br />
                                  <code>{row.projectId}</code>
                                </>
                              ) : null}
                            </div>
                          ) : (
                            <span>-</span>
                          )}
                        </DashboardTableDetailsItem>
                        <DashboardTableDetailsItem label={t('auditDetailEnvironment')}>
                          {row.environmentId ? (
                            <div>
                              <button
                                type="button"
                                className="dashboard-audit-events__copy-value"
                                title={row.environmentId}
                                onClick={() =>
                                  void copyAuditScopeValue(row.environmentId || '', 'Environment')
                                }
                              >
                                {environmentLabel}
                              </button>
                              {environmentLabel !== readText(row.environmentId) ? (
                                <>
                                  <br />
                                  <code>{row.environmentId}</code>
                                </>
                              ) : null}
                            </div>
                          ) : (
                            <span>-</span>
                          )}
                        </DashboardTableDetailsItem>
                      </DashboardTableDetailsGrid>
                      {row.metadata && Object.keys(row.metadata).length > 0 ? (
                        <details className="dashboard-audit-events__raw-metadata">
                          <summary>{t('auditDetailRawJson')}</summary>
                          <pre className="dashboard-data-table__metadata-json">
                            {JSON.stringify(row.metadata, null, 2)}
                          </pre>
                        </details>
                      ) : null}
                    </div>
                  </DashboardTableDetailsPanel>
                </React.Fragment>
              );
            })
          )}
        </DashboardTable>
      </section>
    </div>
  );
}
